import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import { useAsyncState } from '@ygtechnologies/bygr-frontend-component-library';
import { getBuyerProjectInfo } from '@/services/buyer-dashboard';
import { BuyerProjectData } from '@/types';
import { ConfiguratorData, ConfiguratorRoomDto, configuratorKeys, getConfiguratorData } from '@/services/configurator/index';
import { useHistory, useLocation } from 'react-router-dom';
import { useIsImpersonating } from '@/utils/hooks';
import { BuyerRoomDto, useBuyerRoomMap } from '@/services/rooms';
import { ConfiguratorVisualizationRoomDto, useConfiguratorVisualization } from '@/services/configurator-visualization';
import { groupConfiguratorRooms } from './utils/configuratorRooms';

export interface OptionConfiguratorContextType {
    is3d: boolean;
    setIs3d: (is3d: boolean) => void;
    tabsHeight: number;
    setTabsHeight: (height: number) => void;
    selectedDeadlineId: number | null;
    setSelectedDeadlineId: (id: number | null) => void;
    configuratorData?: ConfiguratorData;
    allDeadlinesConfiguratorData?: ConfiguratorData;
    /** Room-grouped view of the selected deadline's groups. Derived, not part of the API payload. */
    rooms: ConfiguratorRoomDto[];
    /** Room-grouped view across all deadlines (for the style-details room tabs). */
    allDeadlineRooms: ConfiguratorRoomDto[];
    /** By-id room metadata lookup, for data not sourced from the grouped `rooms` (signing, style breadcrumbs). */
    getRoomMeta: (id: number) => BuyerRoomDto | undefined;
    /** Unit-wide 3D visualization/mapping data, fetched up-front so a room can render straight into its
     *  visual panel without flashing the list view first. Undefined until the fetch resolves. */
    visualizationRooms?: ConfiguratorVisualizationRoomDto[];
    /** True while the visualization fetch is in flight with no data yet — `Room` holds a loader in this
     *  window instead of rendering the list, to avoid the list→visualization flash on first load. */
    visualizationLoading: boolean;
    projectData: BuyerProjectData | null;
    basePath: string;
    baseUrl: string;
    residentialUnitId: number;
    tabsEnabled: boolean;
    setTabsEnabled: (enabled: boolean) => void;
    bottomPanelEnabled: boolean;
    setBottomPanelEnabled(enabled: boolean): void;
    viewOnly: boolean;
    /// True when this configurator is rendered in the anonymous showroom. Use this for control-flow
    /// decisions (deadline/lock bypasses, status hiding) — it reads clearer than `!!showroomPath`.
    isShowroom: boolean;
    /// True when the configurator uses the immersive full-bleed two-tier presentation — the anonymous
    /// showroom OR the authenticated buyer `/options/configurator` route. Drives presentation only
    /// (two-tier menu, full-bleed layout, compact cards); use `isShowroom` for anonymous/deadline
    /// behavior. The showroom sets both; the authenticated immersive route sets only this.
    immersive: boolean;
    /// Multi-deadline view: instead of showing one deadline at a time, surface every deadline's
    /// rooms/groups in one unified view (the option-group menu is organised into per-deadline
    /// sections). Active in the showroom and the immersive buyer route. When true,
    /// `configuratorData`/`rooms` expose ALL deadlines (and `grandPrice` is the combined total).
    multiDeadline: boolean;
    /// When set, this configurator is rendered in showroom (anonymous) mode. Components that
    /// build product-image URLs read this and route through the path-scoped showroom endpoint
    /// instead of the auth-gated /api/project-catalog-product-file path. Prefer `isShowroom` for
    /// boolean checks; this holds the actual path needed to build showroom URLs.
    /// Value is `<projectSlug>/<unitId>/<hash>`.
    showroomPath?: string | null;
}

export const OptionConfiguratorContext = createContext<OptionConfiguratorContextType | undefined>(undefined);

export const useOptionConfigurator = () => {
    const context = useContext(OptionConfiguratorContext);
    if (!context) {
        throw new Error('useOptionConfigurator must be used within an OptionConfiguratorProvider');
    }
    return context;
};

/**
 * Like {@link useOptionConfigurator} but returns undefined instead of throwing when used outside an
 * OptionConfiguratorProvider. Seller-side pages reuse configurator components (e.g. the product
 * more-info modal) without the provider; there is no showroom in those contexts, so the
 * configurator-derived values are simply absent.
 */
export const useOptionConfiguratorOptional = () => useContext(OptionConfiguratorContext);

export interface OptionConfiguratorProviderProps {
    children: ReactNode;
    residentialUnitId: number;
    basePath: string;
    baseUrl: string;
    // The v2 configurator tracks the selected deadline in the URL (`/deadline/:id`) and navigates
    // between deadlines. The legacy configurator renders every deadline on one page at `basePath`,
    // so it opts out: the deadline is then held in internal state (defaulting to the first deadline
    // with options) and the URL-redirect effect below is skipped.
    manageDeadlineInUrl?: boolean;
    // Renders the immersive full-bleed two-tier presentation (the `/options/configurator` route).
    // Presentation only — data + mutations stay on the authenticated path (`isShowroom` is false).
    immersive?: boolean;
    // Multi-deadline preview: expose every deadline's rooms/groups in one unified view (see
    // `multiDeadline` on the context). The deadline still rides the URL for stable links, but it
    // becomes a vestigial segment — the view shows all deadlines and reorders by the prioritized one.
    multiDeadline?: boolean;
}

export const OptionConfiguratorProvider: React.FC<OptionConfiguratorProviderProps> = ({
    children,
    residentialUnitId,
    basePath,
    baseUrl,
    manageDeadlineInUrl = true,
    immersive = false,
    multiDeadline = false,
}) => {
    const [projectData] = useAsyncState(() => getBuyerProjectInfo(residentialUnitId), [residentialUnitId], null);
    const location = useLocation();
    const history = useHistory();

    // URL-derived deadline (v2). Computed unconditionally to keep hook order stable; only used when
    // manageDeadlineInUrl is true.
    const urlDeadlineId = useMemo(() => {
        const match = location.pathname.match(/\/deadline\/(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }, [location.pathname]);
    // State-held deadline (legacy). Defaulted from projectData by the effect below.
    const [stateDeadlineId, setStateDeadlineId] = useState<number | null>(null);

    const selectedDeadlineId = manageDeadlineInUrl ? urlDeadlineId : stateDeadlineId;

    const setSelectedDeadlineId = React.useCallback(
        (id: number | null) => {
            if (!manageDeadlineInUrl) {
                setStateDeadlineId(id);
                return;
            }
            if (id !== null) {
                history.push(`${basePath}/deadline/${id}`);
            } else {
                history.push(basePath);
            }
        },
        [basePath, history, manageDeadlineInUrl]
    );

    const [is3d, setIs3d] = useState(false);
    const [tabsHeight, setTabsHeight] = useState(273);
    const [tabsEnabled, setTabsEnabled] = useState(true);
    const [bottomPanelEnabled, setBottomPanelEnabled] = useState(true);
    const viewOnly = useIsImpersonating();

    useEffect(() => {
        if (!projectData || selectedDeadlineId !== null) return;
        const firstDeadline = projectData.deadlines.find((d) => d.hasOptionsAttached);
        if (!firstDeadline) return;
        if (manageDeadlineInUrl) {
            // v2: encode the default deadline in the URL.
            if (location.pathname === basePath) {
                history.replace(`${basePath}/deadline/${firstDeadline.id}`);
            }
        } else {
            // legacy: default the state-held deadline.
            setStateDeadlineId(firstDeadline.id);
        }
    }, [projectData, selectedDeadlineId, location.pathname, basePath, history, manageDeadlineInUrl]);

    const { data: rawAllDeadlinesConfiguratorData } = useQuery({
        queryKey: configuratorKeys.allDeadlinesByUnit(residentialUnitId),
        queryFn: () => getConfiguratorData(residentialUnitId),
    });

    const roomsMap = useBuyerRoomMap(residentialUnitId);

    // Kick off the unit's visualization fetch here (not lazily in Room) so it's already resolving —
    // usually resolved — by the time a room mounts after the deadline/room redirects. Room reads the
    // result from context and holds a loader until `visualizationLoading` clears, eliminating the
    // list→visualization flash on first load.
    const { data: visualizationRooms, isLoading: visualizationLoading } =
        useConfiguratorVisualization(residentialUnitId);

    // The API payload is kept API-shaped (flat `groups`); the room-grouped view is derived
    // separately below so it is never confused with data the API actually returned.
    const allDeadlinesConfiguratorData = rawAllDeadlinesConfiguratorData;

    const configuratorData = useMemo<ConfiguratorData | undefined>(() => {
        if (!allDeadlinesConfiguratorData || selectedDeadlineId === null) {
            return undefined;
        }

        const groups = allDeadlinesConfiguratorData.groups.filter((group) => group.deadlineId === selectedDeadlineId);
        const grandPrice = groups.reduce((sum, group) => sum + (group.selectedOption?.price?.finalPrice ?? 0), 0);

        return {
            ...allDeadlinesConfiguratorData,
            deadlineId: selectedDeadlineId,
            groups,
            grandPrice,
        };
    }, [allDeadlinesConfiguratorData, selectedDeadlineId]);

    // Derived room-grouped views. Single place the flat groups are joined with room metadata.
    const rooms = useMemo(
        () => groupConfiguratorRooms(configuratorData?.groups ?? [], roomsMap),
        [configuratorData, roomsMap]
    );
    const allDeadlineRooms = useMemo(
        () => groupConfiguratorRooms(allDeadlinesConfiguratorData?.groups ?? [], roomsMap),
        [allDeadlinesConfiguratorData, roomsMap]
    );
    const getRoomMeta = React.useCallback((id: number) => roomsMap?.get(id), [roomsMap]);

    // Multi-deadline preview exposes the full all-deadlines payload (with the combined grand total)
    // instead of the single-deadline slice, so the configurator renders every deadline's groups at
    // once. The selected deadline stays in the URL only to keep links stable.
    const multiDeadlineData = useMemo<ConfiguratorData | undefined>(() => {
        if (!allDeadlinesConfiguratorData) return undefined;
        const grandPrice = allDeadlinesConfiguratorData.groups.reduce(
            (sum, group) => sum + (group.selectedOption?.price?.finalPrice ?? 0),
            0
        );
        return { ...allDeadlinesConfiguratorData, grandPrice };
    }, [allDeadlinesConfiguratorData]);

    const effectiveConfiguratorData = multiDeadline ? multiDeadlineData : configuratorData;
    const effectiveRooms = multiDeadline ? allDeadlineRooms : rooms;

    const value = useMemo<OptionConfiguratorContextType>(
        () => ({
            is3d,
            setIs3d,
            tabsHeight,
            setTabsHeight,
            selectedDeadlineId,
            setSelectedDeadlineId,
            configuratorData: effectiveConfiguratorData,
            allDeadlinesConfiguratorData,
            rooms: effectiveRooms,
            allDeadlineRooms,
            getRoomMeta,
            visualizationRooms,
            visualizationLoading,
            projectData,
            basePath,
            baseUrl,
            residentialUnitId,
            tabsEnabled,
            setTabsEnabled,
            bottomPanelEnabled,
            setBottomPanelEnabled,
            viewOnly,
            isShowroom: false,
            immersive,
            multiDeadline,
        }),
        [
            is3d,
            tabsHeight,
            selectedDeadlineId,
            setSelectedDeadlineId,
            effectiveConfiguratorData,
            allDeadlinesConfiguratorData,
            effectiveRooms,
            allDeadlineRooms,
            getRoomMeta,
            visualizationRooms,
            visualizationLoading,
            projectData,
            basePath,
            baseUrl,
            residentialUnitId,
            tabsEnabled,
            bottomPanelEnabled,
            viewOnly,
            immersive,
            multiDeadline,
        ]
    );
    if (projectData === null) {
        return null;
    }
    return <OptionConfiguratorContext.Provider value={value}>{children}</OptionConfiguratorContext.Provider>;
};
